import type { ActionType } from "typesafe-actions";
import { createReducer } from "typesafe-actions";

import type { SuppressionList, CompanyParsingErrors } from "../../components/types";
import { CreateSuppressionListStep } from "../../components/types";
import { SuppressionListActions } from "../action-creators";

export type SuppressionListState = {
    lists: SuppressionList[];
    isLoading: boolean;
    isReady: boolean;
    parsing: { error: CompanyParsingErrors; domains: string[]; totalRowsCount: number };
    createListStep: CreateSuppressionListStep;
    sendingError: string;
    fetchListsError: string;
    deletingError: string;
};

const INITIAL_SUPPRESSION_LIST_STATE: SuppressionListState = {
    lists: [],
    isLoading: false,
    isReady: false,
    parsing: {
        error: { isOverTheLimit: false, notADomains: false, format: false, columnsNumber: false },
        domains: [],
        totalRowsCount: 0,
    },
    createListStep: CreateSuppressionListStep.UPLOAD_FILE,
    sendingError: "",
    fetchListsError: "",
    deletingError: "",
};

const defaultSendingError = "Error sending parsed suppression list";
const defaultDeletingError = "Error deleting suppression list";
const defaultFetchListsError = "Error fetching suppression lists";

export const suppressionListReducer = createReducer<SuppressionListState, ActionType<typeof SuppressionListActions>>(
    INITIAL_SUPPRESSION_LIST_STATE,
)
    .handleAction(SuppressionListActions.parseListAsync.success, (state, { payload }) => ({
        ...state,
        parsing: {
            ...state.parsing,
            domains: payload.parsedDomains,
            totalRowsCount: payload.totalRowCount,
        },
    }))
    .handleAction(SuppressionListActions.parseListAsync.failure, (state, { payload }) => ({
        ...state,
        parsing: { ...state.parsing, error: { ...state.parsing.error, ...payload } },
    }))
    .handleAction(SuppressionListActions.resetSuppressionListErrors, (state) => ({
        ...state,
        parsing: { ...state.parsing, error: { ...INITIAL_SUPPRESSION_LIST_STATE.parsing.error } },
        sendingError: INITIAL_SUPPRESSION_LIST_STATE.sendingError,
        deletingError: INITIAL_SUPPRESSION_LIST_STATE.deletingError,
    }))
    .handleAction(SuppressionListActions.sendParsedAsync.request, (state) => ({
        ...state,
        isLoading: true,
        sendingError: "",
    }))
    .handleAction(SuppressionListActions.sendParsedAsync.success, (state, { payload }) => ({
        ...state,
        lists: state.lists.concat(payload),
        isLoading: false,
        isReady: true,
    }))
    .handleAction(SuppressionListActions.sendParsedAsync.failure, (state, { payload }) => ({
        ...state,
        sendingError: payload ?? defaultSendingError,
        isLoading: false,
    }))
    .handleAction(SuppressionListActions.fetchSuppressionListsAsync.request, (state) => ({
        ...state,
        isLoading: true,
    }))
    .handleAction(SuppressionListActions.fetchSuppressionListsAsync.success, (state, { payload }) => ({
        ...state,
        lists: payload,
        isLoading: false,
        isReady: true,
    }))
    .handleAction(SuppressionListActions.fetchSuppressionListsAsync.failure, (state, { payload }) => ({
        ...state,
        isLoading: false,
        fetchListsError: payload ?? defaultFetchListsError,
    }))
    .handleAction(SuppressionListActions.setCreateListStep, (state, { payload }) => ({
        ...state,
        createListStep: payload,
    }))
    .handleAction(SuppressionListActions.resetParsedList, (state) => ({
        ...state,
        parsing: { ...INITIAL_SUPPRESSION_LIST_STATE.parsing },
    }))
    .handleAction(SuppressionListActions.deleteListAsync.request, (state) => ({
        ...state,
        isLoading: true,
    }))
    .handleAction(SuppressionListActions.deleteListAsync.success, (state) => ({
        ...state,
        isLoading: false,
        lists: state.lists.filter((list) => !list.isOwnedByRequestedUser),
    }))
    .handleAction(SuppressionListActions.deleteListAsync.failure, (state, { payload }) => ({
        ...state,
        isLoading: false,
        deletingError: payload ?? defaultDeletingError,
    }));
